import {
  Badge,
  Checkbox,
  Group,
  MultiSelect,
  NumberFormatter,
  Tooltip,
} from "@mantine/core";
import ModelCardTooltip from "./ModelCardTooltip";
import {
  isDeprecated,
  isUnavailable,
  lifecycleMeta,
  lifecycleOf,
} from "./modelLifecycle";
import { IconCheck, IconCurrencyDollar } from "@tabler/icons-react";
import { useStore } from "../store";
import { useState } from "react";

// Hover state is kept per-option so moving the cursor between options doesn't
// re-render the parent MultiSelect — which would wipe Mantine's internal
// searchValue. Tooltip cleanup on click is handled by `keepMounted: false`
// unmounting options.
function ModelOptionRow({ option, checked, iconProps }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Tooltip
      opened={hovered}
      label={<ModelCardTooltip model={option.model} />}
      withArrow
    >
      <Group
        flex="1"
        gap="xs"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Group flex="1" justify="space-between">
          <Group gap="xs" wrap="nowrap">
            {option.label}
            {lifecycleOf(option.model) !== "active" && (
              <Badge
                size="xs"
                variant="light"
                color={lifecycleMeta(option.model).color}
              >
                {lifecycleMeta(option.model).label}
              </Badge>
            )}
          </Group>
          <Group>
            <Badge
              leftSection={<IconCurrencyDollar size={12} />}
              radius="xs"
              color="gray"
              size="xs"
            >
              <NumberFormatter
                value={option.model.pricingGraduations[0].input}
                decimalScale={2}
                fixedDecimalScale
              />
              {" / "}
              <NumberFormatter
                value={option.model.pricingGraduations[0].output}
                decimalScale={2}
                fixedDecimalScale
              />
            </Badge>
          </Group>
        </Group>
        {checked && (
          <IconCheck style={{ marginInlineStart: "auto" }} {...iconProps} />
        )}
      </Group>
    </Tooltip>
  );
}

export default function ModelMultiSelect({ value, onChange, data, ...props }) {
  const [showDeprecated, setShowDeprecated] = useState(false);
  const providers = useStore((state) => state.providers);

  const supportedModels =
    providers?.flatMap((provider) => provider.models) ?? null;

  // Keep already-selected models visible regardless of state. Otherwise:
  // "unavailable" (retired) models can't be called, so they're never offered;
  // deprecated models are still usable but hidden behind a toggle.
  const selected = new Set(Array.isArray(value) ? value : []);
  const filteredModels =
    supportedModels?.filter((model) => {
      if (selected.has(model.name)) return true;
      if (model.isDecision) return false;
      if (isUnavailable(model)) return false;
      if (isDeprecated(model)) return showDeprecated;
      return true;
    }) ?? [];

  data ??= filteredModels?.map((model) => ({
    value: model.name,
    label: model.name,
    model: model,
  }));

  // A model name can be offered by more than one provider (e.g. Gemini and
  // GeminiVertex share the same model descriptors). Mantine's MultiSelect
  // throws on duplicate option values, so collapse to the first occurrence.
  if (data) {
    const seen = new Set();
    data = data.filter((option) => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
  }

  const iconProps = {
    stroke: 1.5,
    color: "currentColor",
    opacity: 0.6,
    size: 18,
  };

  const renderSelectOption = ({ option, checked }) => (
    <ModelOptionRow option={option} checked={checked} iconProps={iconProps} />
  );

  return (
    <MultiSelect
      comboboxProps={{ keepMounted: false }}
      label={
        <Group justify="space-between" align="flex-end">
          Models
          <Checkbox
            size="xs"
            checked={showDeprecated}
            onChange={(event) => setShowDeprecated(event.currentTarget.checked)}
            label="Show deprecated"
          />
        </Group>
      }
      description="Select all models to send this request to"
      placeholder="Pick models"
      value={value}
      data={data}
      onChange={onChange}
      searchable
      renderOption={renderSelectOption}
      {...props}
    />
  );
}
